import type {
  AppointmentDocumentationSnapshotResponse,
  AppointmentWritebackStateResponse,
} from "../../../shared/api-types";

/**
 * The RD-facing Healthie delivery state of one approved Draft (NO-541).
 * `null` is an approved Draft that froze no writeback (writeback unconfigured,
 * or an approval that predates automatic sending). The surface that has no
 * documentation snapshot at all (the Appointmentless Session draft) passes
 * `undefined` and shows no delivery line.
 */
export type SoapDraftDelivery = AppointmentWritebackStateResponse | null;

export function soapDraftDeliveryFromSnapshot(
  snapshot: AppointmentDocumentationSnapshotResponse | null
): SoapDraftDelivery {
  return snapshot ? snapshot.writeback : null;
}

/**
 * Approval freezes attempt 1 inside the approval transaction, so the truthful
 * state right after a successful approval is "sending" even before the next
 * snapshot read lands. Until that first read arrives, a `null` is stale, not
 * an answer; the first read after approval replaces it, so an environment
 * without writeback cannot stick on "sending".
 */
export function presentedSoapDraftDelivery(
  delivery: SoapDraftDelivery,
  awaitingFirstReadAfterApproval: boolean
): SoapDraftDelivery {
  if (awaitingFirstReadAfterApproval && delivery === null) {
    return { state: "in_progress" };
  }

  return delivery;
}

/** The words of the delivered line's detail that open the note in Healthie (NO-679). */
export const SIGN_IN_HEALTHIE_LINK_TEXT = "sign it in Healthie";

/**
 * The same-origin click-through that records the read and redirects to the
 * delivered Healthie chart note. `rdId` carries the selected-RD lane.
 */
export function healthieNoteClickThroughPath(
  appointmentId: string,
  rdId?: string
): string {
  const path = `/api/appointments/${encodeURIComponent(appointmentId)}/documentation/healthie-note`;

  return rdId === undefined ? path : `${path}?rdId=${encodeURIComponent(rdId)}`;
}

export type SoapDraftDeliveryTone = "neutral" | "success" | "danger" | "warning";

export interface SoapDraftDeliveryLine {
  tone: SoapDraftDeliveryTone;
  /** The one-clause headline, bold. */
  headline: string;
  /** What, if anything, the RD should do next. */
  detail: string;
}

/**
 * Plain words for each closed delivery state. Red (`danger`) is reserved for a
 * problem the RD can fix themselves — a collision to review in Healthie, or a
 * failed send they can finish by hand with Copy note. Amber marks a case that
 * needs someone else to check the chart first, and the RD must not act on it.
 * Nothing here ever carries a vendor message, a Patient identifier, or SOAP
 * content: the only identifier is the Healthie note the RD may need to find.
 */
export function soapDraftDeliveryLine(
  delivery: SoapDraftDelivery
): SoapDraftDeliveryLine {
  if (delivery === null) {
    return {
      tone: "neutral",
      headline: "Not sent to Healthie automatically.",
      detail: "Copy the note into Healthie instead.",
    };
  }

  switch (delivery.state) {
    case "in_progress":
      return {
        tone: "neutral",
        headline: "Sending to Healthie…",
        detail: "This can take a while if Healthie is slow.",
      };
    case "delivered":
      return {
        tone: "success",
        headline: `Sent to Healthie as note ${delivery.healthieNoteId}.`,
        detail: "It's unlocked and unsigned, so sign it in Healthie.",
      };
    case "collision":
      return {
        tone: "danger",
        headline: `Not sent: note ${delivery.healthieNoteId} already exists in this appointment's SOAP form.`,
        detail: "Nothing was overwritten. Review that note in Healthie.",
      };
    case "failed":
      return failedDeliveryLine(delivery.nextStep);
  }
}

function failedDeliveryLine(
  nextStep: Extract<AppointmentWritebackStateResponse, { state: "failed" }>["nextStep"]
): SoapDraftDeliveryLine {
  if (nextStep === "copy_by_hand") {
    return {
      tone: "danger",
      headline: "Sending to Healthie failed.",
      detail: "Copy the note and add it in Healthie yourself.",
    };
  }

  return {
    tone: "warning",
    headline: "We couldn't confirm whether this note reached Healthie.",
    detail: "Don't add it by hand until it has been checked.",
  };
}
